import { fetchConfig } from '@theme/utilities';
import { formatMoney } from '@theme/money-formatting';

/**
 * Bouwer voor een Sinterklaasslinger (sections/slinger-bouwer.liquid), opgebouwd zoals assets/naamslinger-bouwer.js.
 *
 * 1. Stijl: Traditioneel of Neutraal. Zet elke kaart en elk item aan het touw met die optiewaarde in die stijl.
 * 2. Items: op elke kaart kies je de variant (pillen), de foto wisselt mee. Aantikken hangt het item rechts aan
 *    het touw, slepen naar het touw hangt het waar je loslaat (tussen de andere items). Het kruisje rechtsboven
 *    (of tikken op het item) haalt het weg.
 *
 * In de winkelmand komt elk item als eigen regel met precies de gekozen variant. Elke regel krijgt de eigenschap
 * "Plek aan de slinger", de regel van het eerste item ook "Volgorde" (het werkbriefje voor Elif).
 * Bedragen zijn in centen.
 */

const OPSLAG = 'sinterklaas-bouwer';
const MAX_ITEMS = 20;

// Vorm van de draad in de SVG (viewBox 0 0 100 100, zonder vaste verhouding uitgerekt): M0,6 Q50,182 100,6.
// De hoogte op positie t (0 tot 1) is DRAAD_BEGIN + DRAAD_DIEPTE * 4 * t * (1 - t), als deel van de SVG-hoogte.
// Zelfde kromme als in assets/naamslinger-bouwer.js.
const DRAAD_BEGIN = 0.06;
const DRAAD_DIEPTE = 0.88;

/** @typedef {{ id: number, opties: string[], prijs: number, beschikbaar: boolean, kaart: string|null, foto: string|null }} Variant */
/** @typedef {{ titel: string, standaard: number, enkel: boolean, varianten: Variant[] }} Item */

class SlingerBouwer extends HTMLElement {
  /** @type {number[]} variant-id's aan het touw, van links naar rechts */ rij = [];
  /** @type {number[]} gekozen variant per kaart */ kaartKeuze = [];
  /** @type {string} gekozen stijl in stap 1 */ stijl = '';
  bezig = false;
  /** @type {number|null} plek van het item dat net is toegevoegd, voor een korte animatie */ nieuw = null;
  #sleep = null;
  #netGesleept = false;

  connectedCallback() {
    try {
      this.data = JSON.parse(this.querySelector('[data-bouwer-data]')?.textContent || '{}');
    } catch {
      return;
    }
    this.geldformaat = this.dataset.moneyFormat || '{{amount}}';
    this.valuta = this.dataset.currency || 'EUR';
    this.hangers = this.querySelector('[data-hangers]');
    this.baan = this.querySelector('[data-baan]');
    this.strook = this.querySelector('[data-strook]');
    this.aanwijzing = this.querySelector('[data-aanwijzing]');
    this.samenvatting = this.querySelector('[data-samenvatting]');
    this.totaal = this.querySelector('[data-totaal]');
    this.melding = this.querySelector('[data-melding]');
    this.knop = this.querySelector('[data-toevoegen]');
    this.kaarten = [...this.querySelectorAll('[data-kaart]')];
    if (!this.hangers || !this.knop) return;

    /** @type {Item[]} */
    this.itemLijst = Array.isArray(this.data.items) ? this.data.items : [];
    /** @type {Map<number, { k: number, p: Item, v: Variant }>} variant-id naar kaart, product en variant */
    this.varianten = new Map();
    this.itemLijst.forEach((p, k) => p.varianten.forEach((v) => this.varianten.set(v.id, { k, p, v })));
    this.kaartKeuze = this.itemLijst.map((p) => p.standaard);
    /** @type {string[]} */
    this.stijlen = Array.isArray(this.data.stijlen) ? this.data.stijlen : [];
    /** @type {Record<string, string>} teksten in de taal van de bezoeker (locales, elif.js.*) */
    this.teksten = this.data.teksten || {};

    this.#herstel();
    this.kaarten.forEach((_, k) => this.#tekenKaart(k));

    this.addEventListener('click', (e) => this.#bijKlik(e));
    this.addEventListener('pointerdown', (e) => this.#startSleep(e));
    if ('ResizeObserver' in window && this.strook) {
      new ResizeObserver(() => this.#hangAanDraad()).observe(this.strook);
    }
    this.#teken();
  }

  /* ---------- Invoer ---------- */

  /** @param {MouseEvent} event */
  #bijKlik(event) {
    if (!(event.target instanceof Element) || this.bezig) return;
    const knop = event.target.closest('button');
    if (!knop || !this.contains(knop)) return;

    if (knop.hasAttribute('data-toevoegen')) return void this.#naarWinkelmand();

    if (knop.hasAttribute('data-stijl')) return void this.#kiesStijl(knop.dataset.stijl || '');

    if (knop.hasAttribute('data-weg')) {
      const plek = Number(knop.dataset.weg);
      const [id] = this.rij.splice(plek, 1);
      if (id !== undefined) this.#toonMelding(this.#t('weg', { naam: this.#itemNaam(id) }));
      this.#bewaar();
      return void this.#teken();
    }

    const kaart = knop.closest('[data-kaart]');
    if (!(kaart instanceof HTMLElement)) return;
    const k = Number(kaart.dataset.kaart);

    if (knop.hasAttribute('data-waarde')) {
      const optie = knop.closest('[data-optie]');
      if (!(optie instanceof HTMLElement)) return;
      return void this.#kiesOptie(k, Number(optie.dataset.optie), knop.dataset.waarde || '');
    }

    if (knop.hasAttribute('data-erbij')) {
      // Een klik die het einde van een sleep is, telt niet nog eens.
      if (this.#netGesleept) return;
      this.#erbij(this.kaartKeuze[k]);
    }
  }

  /**
   * Variant van een item die past bij de gekozen waarde voor optie i, met de andere opties zoveel mogelijk gelijk.
   * @param {Item} p
   * @param {string[]} huidig
   * @param {number} i
   * @param {string} waarde
   */
  #zoekVariant(p, huidig, i, waarde) {
    const gewenst = [...huidig];
    gewenst[i] = waarde;
    return (
      p.varianten.find((x) => x.opties.every((o, j) => o === gewenst[j])) ||
      p.varianten.find((x) => x.opties[i] === waarde && x.beschikbaar) ||
      p.varianten.find((x) => x.opties[i] === waarde)
    );
  }

  /** Stap 1: zet elke kaart en elk item aan het touw met deze optiewaarde in de gekozen stijl. */
  #kiesStijl(stijl) {
    if (!stijl) return;
    this.stijl = stijl;
    /** @param {number} id */
    const inStijl = (id) => {
      const info = this.varianten.get(id);
      if (!info) return id;
      if (info.v.opties.includes(stijl)) return id;
      // Zoek de optie waarin deze stijl als waarde voorkomt; items zonder die optie blijven zoals ze zijn.
      for (let j = 0; j < info.v.opties.length; j++) {
        if (info.p.varianten.some((x) => x.opties[j] === stijl)) {
          return this.#zoekVariant(info.p, info.v.opties, j, stijl)?.id ?? id;
        }
      }
      return id;
    };
    this.kaartKeuze = this.kaartKeuze.map(inStijl);
    const voor = this.rij.join();
    this.rij = this.rij.map(inStijl);
    this.kaarten.forEach((_, k) => this.#tekenKaart(k));
    this.#toonMelding(
      this.#t(voor !== this.rij.join() ? 'stijl_nu' : 'stijl_nieuw', { stijl: stijl.toLowerCase() })
    );
    this.#bewaar();
    this.#teken();
  }

  /**
   * Kies een optiewaarde op een kaart en zoek de variant die bij alle gekozen waarden past.
   * @param {number} k kaart
   * @param {number} i optie
   * @param {string} waarde
   */
  #kiesOptie(k, i, waarde) {
    const p = this.itemLijst[k];
    if (!p) return;
    const huidig = this.varianten.get(this.kaartKeuze[k])?.v.opties || p.varianten[0]?.opties || [];
    const v = this.#zoekVariant(p, huidig, i, waarde);
    if (!v) return;
    this.kaartKeuze[k] = v.id;
    this.#tekenKaart(k);
    this.#bewaar();
  }

  /** Kaart bijwerken naar de gekozen variant: gekozen waarde, foto, prijs en knop. */
  #tekenKaart(k) {
    const kaart = this.kaarten[k];
    const info = this.varianten.get(this.kaartKeuze[k]);
    if (!kaart || !info) return;
    const { v } = info;
    kaart.dataset.variant = String(v.id);
    for (const optie of kaart.querySelectorAll('[data-optie]')) {
      const waarde = v.opties[Number(/** @type {HTMLElement} */ (optie).dataset.optie)];
      const label = optie.querySelector('[data-optie-waarde]');
      if (label) label.textContent = waarde;
      for (const b of optie.querySelectorAll('[data-waarde]')) {
        b.setAttribute('aria-pressed', String(/** @type {HTMLElement} */ (b).dataset.waarde === waarde));
      }
    }
    const foto = kaart.querySelector('[data-item-foto]');
    if (foto instanceof HTMLImageElement && v.kaart && foto.getAttribute('src') !== v.kaart) {
      foto.src = v.kaart;
      foto.alt = this.#itemNaam(v.id);
    }
    const prijs = kaart.querySelector('[data-kaart-prijs]');
    if (prijs) prijs.textContent = this.#bedrag(v.prijs);
    for (const b of kaart.querySelectorAll('[data-erbij]')) {
      /** @type {HTMLButtonElement} */ (b).disabled = !v.beschikbaar;
      if (b.classList.contains('slinger-bouwer__kaart-actie')) b.textContent = v.beschikbaar ? this.#t('aan_de_slinger') : this.#t('uitverkocht');
    }
  }

  /**
   * Hang een item (variant) aan het touw.
   * @param {number} id variant-id
   * @param {number} [plek] waar losgelaten; zonder plek rechts achteraan
   */
  #erbij(id, plek) {
    const info = this.varianten.get(id);
    if (!info || !info.v.beschikbaar) return;
    if (this.rij.length >= MAX_ITEMS) {
      this.#toonMelding(this.#t('max_items', { max: MAX_ITEMS }));
      return;
    }
    const index = plek === undefined ? this.rij.length : Math.min(Math.max(plek, 0), this.rij.length);
    this.rij.splice(index, 0, id);
    this.nieuw = index;
    this.#toonMelding(this.#t('plek', { naam: this.#itemNaam(id), n: index + 1 }));
    this.#bewaar();
    this.#teken();
  }

  /* ---------- Slepen van een item naar het touw ---------- */

  /** @param {PointerEvent} event */
  #startSleep(event) {
    if (this.bezig || this.#sleep || event.button !== 0 || !(event.target instanceof Element)) return;
    const foto = event.target.closest('[data-item-foto]');
    const kaart = event.target.closest('[data-kaart]');
    if (!(foto instanceof HTMLImageElement) || !(kaart instanceof HTMLElement)) return;
    const sleep = {
      id: this.kaartKeuze[Number(kaart.dataset.kaart)],
      foto,
      x: event.clientX,
      y: event.clientY,
      pointerId: event.pointerId,
      actief: false,
      aanraking: event.pointerType === 'touch',
      timer: 0,
      spook: /** @type {HTMLElement|null} */ (null),
    };
    this.#sleep = sleep;
    if (sleep.aanraking) sleep.timer = window.setTimeout(() => this.#activeer(sleep.x, sleep.y), 220);
    this._move = (e) => this.#volg(e);
    this._up = (e) => this.#eind(e);
    this._cancel = () => this.#stop();
    window.addEventListener('pointermove', this._move);
    window.addEventListener('pointerup', this._up);
    window.addEventListener('pointercancel', this._cancel);
  }

  #activeer(x, y) {
    const s = this.#sleep;
    if (!s || s.actief) return;
    s.actief = true;
    window.clearTimeout(s.timer);
    const spook = document.createElement('div');
    spook.className = 'slinger-bouwer__spook';
    const img = document.createElement('img');
    const vrij = this.data.uitsnedes?.[String(s.id)];
    if (vrij) spook.classList.add('slinger-bouwer__spook--vrij');
    img.src = vrij?.url || this.varianten.get(s.id)?.v.foto || s.foto.currentSrc || s.foto.src;
    img.alt = '';
    spook.append(img);
    document.body.append(spook);
    s.spook = spook;
    document.documentElement.classList.add('slinger-bouwer-sleept');
    this.#plaats(x, y);
  }

  #plaats(x, y) {
    const s = this.#sleep;
    if (!s?.spook) return;
    s.spook.style.transform = `translate(${x}px, ${y}px) translate(-50%, -50%)`;
    this.strook?.classList.toggle('slinger-bouwer__strook--doel', this.#boven(x, y));
  }

  #boven(x, y) {
    if (!this.strook) return false;
    const r = this.strook.getBoundingClientRect();
    return x >= r.left && x <= r.right && y >= r.top && y <= r.bottom;
  }

  /** @param {PointerEvent} e */
  #volg(e) {
    const s = this.#sleep;
    if (!s || e.pointerId !== s.pointerId) return;
    const afstand = Math.hypot(e.clientX - s.x, e.clientY - s.y);
    if (!s.actief) {
      if (s.aanraking) {
        if (afstand > 10) this.#stop();
        return;
      }
      if (afstand < 6) return;
      this.#activeer(e.clientX, e.clientY);
    }
    e.preventDefault();
    this.#plaats(e.clientX, e.clientY);
  }

  /** @param {PointerEvent} e */
  #eind(e) {
    const s = this.#sleep;
    if (!s || e.pointerId !== s.pointerId) return;
    const erop = s.actief && this.#boven(e.clientX, e.clientY);
    const gesleept = s.actief;
    this.#stop();
    if (gesleept) {
      this.#netGesleept = true;
      window.setTimeout(() => (this.#netGesleept = false), 0);
    }
    if (erop) this.#erbij(s.id, this.#plekBij(e.clientX));
  }

  #stop() {
    const s = this.#sleep;
    if (!s) return;
    window.clearTimeout(s.timer);
    s.spook?.remove();
    this.strook?.classList.remove('slinger-bouwer__strook--doel');
    document.documentElement.classList.remove('slinger-bouwer-sleept');
    window.removeEventListener('pointermove', this._move);
    window.removeEventListener('pointerup', this._up);
    window.removeEventListener('pointercancel', this._cancel);
    this.#sleep = null;
  }

  /**
   * Plek aan het touw bij een horizontale positie: het aantal items waarvan het midden links van x ligt.
   * @param {number} x
   */
  #plekBij(x) {
    return [...this.hangers.children].filter((el) => {
      const r = el.getBoundingClientRect();
      return r.left + r.width / 2 < x;
    }).length;
  }

  /* ---------- Tekenen ---------- */

  #teken() {
    this.hangers.replaceChildren(
      ...this.rij.map((id, plek) => {
        const li = document.createElement('li');
        li.className = 'slinger-bouwer__hanger';
        li.dataset.variant = String(id);
        if (this.nieuw === plek) li.classList.add('slinger-bouwer__hanger--nieuw');
        const knop = document.createElement('button');
        knop.type = 'button';
        knop.dataset.weg = String(plek);
        const naam = this.#itemNaam(id);
        knop.setAttribute('aria-label', this.#t('item_aria', { naam, n: plek + 1 }));
        knop.title = this.#t('tik_weg', { naam });
        const vak = document.createElement('span');
        vak.className = 'slinger-bouwer__item-foto';
        // Vrijstaande uitsnede (bin/knuffels-uitsnede.py): het item hangt zonder fotoachtergrond aan het touw.
        const vrij = this.data.uitsnedes?.[String(id)];
        const foto = vrij?.url || this.varianten.get(id)?.v.foto;
        if (foto) {
          const img = document.createElement('img');
          img.src = foto;
          img.alt = '';
          img.width = vrij ? vrij.b : 400;
          img.height = vrij ? vrij.h : 533;
          vak.append(img);
        }
        if (vrij) {
          vak.classList.add('slinger-bouwer__item-foto--vrij');
          li.classList.add('slinger-bouwer__hanger--vrij');
        }
        // Kijkt het item op de foto naar buiten, dan gespiegeld zodat het naar de slinger toe kijkt: links van het
        // midden naar rechts, rechts van het midden naar links (WhatsApp Elif 15-10 17:50, zoals bij de naamslinger).
        if (vrij?.k) {
          const midden = (this.rij.length - 1) / 2;
          const moet = plek < midden ? 'rechts' : plek > midden ? 'links' : null;
          if (moet && vrij.k !== moet) vak.classList.add('slinger-bouwer__item-foto--gespiegeld');
        }
        knop.append(vak);
        // Klein rond kruisje rechtsboven om het item weg te halen (tikvlak 32 px).
        const kruis = document.createElement('button');
        kruis.type = 'button';
        kruis.className = 'slinger-bouwer__kruis';
        kruis.dataset.weg = String(plek);
        kruis.setAttribute('aria-label', this.#t('verwijderen', { naam }));
        kruis.title = this.#t('verwijderen', { naam });
        const teken = document.createElement('span');
        teken.setAttribute('aria-hidden', 'true');
        kruis.append(teken);
        li.append(knop, kruis);
        return li;
      })
    );
    this.nieuw = null;

    if (this.aanwijzing) this.aanwijzing.hidden = this.rij.length > 0;
    for (const b of this.querySelectorAll('[data-stijl]')) {
      b.setAttribute('aria-pressed', String(/** @type {HTMLElement} */ (b).dataset.stijl === this.stijl));
    }
    this.#tekenBalk();
    // Direct meten en zetten (niet pas bij het volgende beeld), zodat de boog ook klopt als het tabblad op de achtergrond staat.
    this.#hangAanDraad();
  }

  /** Zet elk item op de hoogte van de boog op die plek, zodat alles de doorhangende draad volgt. */
  #hangAanDraad() {
    if (!(this.baan instanceof HTMLElement) || !(this.hangers instanceof HTMLElement)) return;
    const breedte = this.baan.offsetWidth;
    const draad = this.baan.querySelector('.slinger-bouwer__draad');
    const draadHoogte = draad ? draad.getBoundingClientRect().height : 0;
    if (!breedte || !draadHoogte) return;
    const stukken = /** @type {HTMLElement[]} */ ([...this.hangers.children]);
    const basis = this.hangers.offsetLeft;
    // Eerst alle plekken meten, dan pas schrijven.
    const hoogtes = stukken.map((el) => {
      const t = Math.min(1, Math.max(0, (basis + el.offsetLeft + el.offsetWidth / 2) / breedte));
      return draadHoogte * (DRAAD_BEGIN + DRAAD_DIEPTE * 4 * t * (1 - t));
    });
    stukken.forEach((el, i) => el.style.setProperty('--zak', `${hoogtes[i].toFixed(1)}px`));
  }

  #tekenBalk() {
    const aantal = this.rij.length;
    const som = this.rij.reduce((t, id) => t + (this.varianten.get(id)?.v.prijs || 0), 0);
    if (this.samenvatting) {
      this.samenvatting.textContent = aantal
        ? this.#t(aantal === 1 ? 'samenvatting_een' : 'samenvatting_meer', { n: aantal, slinger: this.data.slinger || '' })
        : this.#t('kies_items');
    }
    if (this.totaal) {
      this.totaal.hidden = aantal === 0;
      this.totaal.textContent = this.#bedrag(som);
    }
    this.knop.disabled = this.bezig || aantal === 0;
  }

  /* ---------- Winkelmand ---------- */

  async #naarWinkelmand() {
    if (this.bezig || this.rij.length === 0) return;
    this.bezig = true;
    this.knop.textContent = this.#t('bezig');
    this.#tekenBalk();

    const volgorde = this.rij.map((id) => this.#itemNaam(id)).join(', ');

    /** @type {Map<number, number[]>} variant-id naar plekken aan het touw (vanaf 1) */
    const plekken = new Map();
    this.rij.forEach((id, i) => plekken.set(id, [...(plekken.get(id) || []), i + 1]));

    // Eén regel per variant, in de volgorde waarin ze voor het eerst aan het touw hangen.
    const items = [...plekken].map(([id, rij], n) => {
      /** @type {Record<string, string>} */
      const properties = { 'Plek aan de slinger': this.#t('plek_van', { plekken: rij.join(', '), totaal: this.rij.length }) };
      if (n === 0) properties.Volgorde = volgorde;
      return { id, quantity: rij.length, properties };
    });

    try {
      // Theme staat als const in snippets/scripts.liquid (niet op window); op /en/ geeft dat /en/cart.
      const routes = (typeof Theme !== 'undefined' && Theme.routes) || window.Theme?.routes || {};
      const antwoord = await fetch(routes.cart_add_url || '/cart/add.js', fetchConfig('json', { body: JSON.stringify({ items }) }));
      const inhoud = await antwoord.json().catch(() => null);
      if (!antwoord.ok || (inhoud && typeof inhoud.status === 'number' && inhoud.status >= 400)) {
        throw new Error(inhoud?.description || inhoud?.message || `Status ${antwoord.status}`);
      }
      try {
        window.sessionStorage.removeItem(this.#opslag());
      } catch {}
      window.location.href = routes.cart_url || '/cart';
    } catch (fout) {
      this.bezig = false;
      this.knop.textContent = this.#t('in_winkelmand');
      this.#toonMelding(this.#t('mislukt'));
      console.warn('[slinger-bouwer]', fout);
      this.#tekenBalk();
    }
  }

  /* ---------- Hulp ---------- */

  /** Naam met gekozen variant, bijvoorbeeld "Mijter (Traditioneel)" of "Staf". */
  #itemNaam(id) {
    const info = this.varianten.get(id);
    if (!info) return 'Item';
    return info.p.enkel ? info.p.titel : `${info.p.titel} (${info.v.opties.join(', ')})`;
  }

  /**
   * Tekst in de taal van de bezoeker (uit de sectie, locales elif.js.*), met [naam] enz. ingevuld.
   * @param {string} sleutel
   * @param {Record<string, string|number>} [waarden]
   */
  #t(sleutel, waarden = {}) {
    let tekst = this.teksten?.[sleutel] ?? sleutel;
    for (const [k, w] of Object.entries(waarden)) tekst = tekst.split(`[${k}]`).join(String(w));
    return tekst;
  }

  #toonMelding(tekst) {
    if (!this.melding) return;
    this.melding.textContent = tekst;
    this.melding.hidden = !tekst;
  }

  #bedrag(centen) {
    try {
      return formatMoney(centen, this.geldformaat || '{{amount}}', this.valuta || 'EUR');
    } catch {
      return `€ ${(centen / 100).toFixed(2).replace('.', ',')}`;
    }
  }

  #bewaar() {
    try {
      window.sessionStorage.setItem(this.#opslag(), JSON.stringify({ v: 1, s: this.stijl, r: this.rij, kk: this.kaartKeuze }));
    } catch {}
  }

  /** Eigen opslag per bouwer (collectie), zodat Sinterklaas en Kerst elkaars slinger niet wissen. Jamy 15-10. */
  #opslag() {
    return this.data?.sleutel ? `${OPSLAG}:${this.data.sleutel}` : OPSLAG;
  }

  #herstel() {
    try {
      const d = JSON.parse(window.sessionStorage.getItem(this.#opslag()) || 'null');
      if (!d) return;
      if (this.stijlen.includes(d.s)) this.stijl = d.s;
      this.rij = Array.isArray(d.r) ? d.r.filter((id) => this.varianten.has(id)).slice(0, MAX_ITEMS) : [];
      if (Array.isArray(d.kk)) {
        d.kk.forEach((id, k) => {
          if (this.varianten.get(id)?.k === k) this.kaartKeuze[k] = id;
        });
      }
    } catch {}
  }
}

if (!customElements.get('slinger-bouwer')) customElements.define('slinger-bouwer', SlingerBouwer);
